import {
  Box,
  Button,
  Alert,
  CircularProgress,
  Autocomplete,
  TextField,
  Typography,
  Paper,
} from "@mui/material";
import { useState, useEffect } from "react";

import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";

import { getAttachmentTypes } from "../../services/AttachmentTypeService";
import { getSourceTypeDefinitions } from "../../services/AttachmentSourceTypeService";
import { uploadAttachmentGeneratedSingle } from "../../services/LegalFileAttachmentService";
import { getLawyerSessionStatus } from "../../services/LawyerService";
import TemplateEditor from "../General/TemplateEditor";
import SourcePicker from "./SourcePicker";
import { useToast } from "../../context/ToastContext";
import LawyerSessionRequiredDialog from "../General/Dialog/LawyerSessionRequiredDialog";

const STAGE_LABELS = {
  stage_1: "Etapa 1",
  stage_2: "Etapa 2",
  stage_3: "Etapa 3",
  stage_4: "Etapa 4",
};

export default function UploadSacAttachmentSingle({ legalFile, onDone }) {
  const { showToast } = useToast();
  const [typesAll, setTypesAll] = useState([]);
  const [types, setTypes] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [sourceDefinitions, setSourceDefinitions] = useState([]);
  const [selectedSources, setSelectedSources] = useState([]);
  const [templateHtml, setTemplateHtml] = useState("");
  const [selectedCertificate, setSelectedCertificate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checkingSession, setCheckingSession] = useState(false);
  const [sessionRequiredLawyer, setSessionRequiredLawyer] = useState(null);

  const certificates = (legalFile?.notification_certificates ?? []).filter(
    (c) => !!c.code,
  );

  useEffect(() => {
    getAttachmentTypes()
      .then((res) => setTypesAll(res?.data?.data ?? []))
      .catch(() => setTypesAll([]));
    getSourceTypeDefinitions()
      .then((res) => setSourceDefinitions(res?.data?.data ?? []))
      .catch(() => setSourceDefinitions([]));
  }, []);

  useEffect(() => {
    setTypes(typesAll.filter((t) => t.allow_online_upload));
    setSelectedType(null);
  }, [typesAll]);

  useEffect(() => {
    if (selectedType) {
      setTemplateHtml(selectedType.generate_template || selectedType.text || "");
      setSelectedSources(
        (selectedType.source_configs ?? []).map((sc) => sc.source_type),
      );
    } else {
      setTemplateHtml("");
      setSelectedSources([]);
    }
    setSelectedCertificate(null);
  }, [selectedType]);

  const getSourceConfig = (sourceType) =>
    (selectedType?.source_configs ?? []).find((sc) => sc.source_type === sourceType) ?? null;

  const isFixed = (sourceType) => {
    const cfg = getSourceConfig(sourceType);
    return cfg !== null && !cfg.is_user_selectable;
  };

  const toggleSource = (sourceType) => {
    if (isFixed(sourceType)) return;
    setSelectedSources((prev) =>
      prev.includes(sourceType)
        ? prev.filter((s) => s !== sourceType)
        : [...prev, sourceType],
    );
  };

  const paramSources = sourceDefinitions.filter(
    (src) =>
      selectedSources.includes(src.source_type) &&
      src.resolver_strategy === "legal_file_relation_param",
  );
  const hasParamSource = paramSources.length > 0;

  const canSubmit = !loading && !checkingSession && !!selectedType;

  const getCertLabel = (cert) => {
    const stage = STAGE_LABELS[cert.stage] || cert.stage || "";
    const date = cert.created_at
      ? new Date(cert.created_at).toLocaleDateString("es-AR")
      : "";
    return [cert.code, stage, date].filter(Boolean).join(" · ");
  };

  const dispatchUpload = async () => {
    setLoading(true);
    try {
      const res = await uploadAttachmentGeneratedSingle({
        legal_file_id:    legalFile.id,
        type_id:          selectedType.id,
        template_html:    templateHtml,
        selected_sources: selectedSources,
        code_param:       selectedCertificate?.code ?? "",
      });
      showToast(res.data?.message ?? "Procesamiento iniciado en segundo plano.", "success");
      setSelectedType(null);
      setSelectedCertificate(null);
      if (onDone) onDone();
    } catch (err) {
      showToast(
        err.response?.data?.message ?? "Error inesperado al enviar la solicitud.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;

    const lawyer = legalFile?.lawyers?.[0];
    if (!lawyer) {
      await dispatchUpload();
      return;
    }

    setCheckingSession(true);
    try {
      const res = await getLawyerSessionStatus(lawyer.id);
      if (!res.data?.data?.cached) {
        setSessionRequiredLawyer(lawyer);
        return;
      }
    } catch {
      // Si falla la consulta de estado, se sigue igual.
    } finally {
      setCheckingSession(false);
    }

    await dispatchUpload();
  };

  const blocked = typesAll.length > 0 && types.length === 0;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <LawyerSessionRequiredDialog
        open={Boolean(sessionRequiredLawyer)}
        onClose={() => setSessionRequiredLawyer(null)}
        lawyer={sessionRequiredLawyer}
        onCaptured={() => dispatchUpload()}
      />

      {blocked && (
        <Alert severity="warning">No hay tipos habilitados para subida en línea a SAC.</Alert>
      )}

      <Autocomplete
        options={types}
        getOptionLabel={(o) => o.display_name || o.name || ""}
        renderInput={(params) => (
          <TextField {...params} label="Tipo de escrito" fullWidth />
        )}
        value={selectedType}
        onChange={(_, v) => setSelectedType(v)}
        disabled={blocked || typesAll.length === 0}
      />

      {selectedType && (
        <>
          {/* Archivos que acompañan al escrito */}
          <SourcePicker
            sourceDefinitions={sourceDefinitions}
            sourceConfigs={selectedType.source_configs ?? []}
            selected={selectedSources}
            onToggle={toggleSource}
            getHint={(src, isSelected) =>
              src.resolver_strategy === "legal_file_relation_param" && isSelected
                ? "Elegí la cédula abajo"
                : null
            }
          />

          {/* Selector de cédula específica */}
          {hasParamSource && (
            <Autocomplete
              options={certificates}
              getOptionLabel={getCertLabel}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Cédula de notificación"
                  helperText={
                    certificates.length === 0
                      ? "Este expediente no tiene cédulas con código registrado."
                      : "Seleccioná la cédula a incluir. Si no seleccionás ninguna se usa la más reciente."
                  }
                />
              )}
              value={selectedCertificate}
              onChange={(_, v) => setSelectedCertificate(v)}
              noOptionsText="Sin cédulas disponibles"
              clearOnEscape
            />
          )}

          {/* Plantilla */}
          <Paper
            elevation={0}
            sx={(theme) => ({
              p: 1.5,
              borderRadius: 3,
              background:
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.04)"
                  : "rgba(255,255,255,0.75)",
              border:
                theme.palette.mode === "dark"
                  ? "1px solid rgba(255,255,255,0.08)"
                  : "1px solid rgba(0,0,0,0.08)",
            })}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.2 }}>
              <DescriptionOutlinedIcon fontSize="small" sx={{ color: "primary.main" }} />
              <Typography sx={{ fontWeight: 800, fontSize: 13.5 }}>Plantilla del escrito</Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
              <Button
                startIcon={<RefreshOutlinedIcon />}
                onClick={() =>
                  setTemplateHtml(selectedType.generate_template || selectedType.text || "")
                }
                variant="outlined"
                size="small"
                sx={{ borderRadius: 2.2, fontWeight: 800, textTransform: "none" }}
              >
                Restaurar original
              </Button>
            </Box>
            <TemplateEditor value={templateHtml} onChange={setTemplateHtml} />
          </Paper>
        </>
      )}

      <Button
        variant="contained"
        color="primary"
        onClick={handleSubmit}
        disabled={!canSubmit}
        fullWidth
      >
        {loading ? <CircularProgress size={24} /> : "Iniciar carga en SAC"}
      </Button>
    </Box>
  );
}
